// @flow
import React, {type Node, useCallback, useRef, Fragment, lazy, Suspense} from 'react'
import {type RelayFragmentContainer, createFragmentContainer, graphql} from 'react-relay'

import ModalLoader from 'components/ui/ModalLoader'
import updateRailsViewTensionRow from 'utils/MiscUtils/updateRailsViewTensionRow'
import TensionProcessModal from './TensionProcessModal'
import AgendaProcessModal from './AgendaProcessModal'
import type {
  ProcessingModals_organization as Organization,
} from './__generated__/ProcessingModals_organization.graphql'
import type {
  ProcessingModals_tension as Tension,
} from './__generated__/ProcessingModals_tension.graphql'
import type {
  ProcessingModals_viewer as Viewer,
} from './__generated__/ProcessingModals_viewer.graphql'

const LazyProposalCreator = lazy(() => import(
  /* webpackPrefetch: true */
  /* webpackChunkName: "ProposalCreator" */
  'components/pages/Proposals/ProposalsView/ProposalCreator'
))

const LazyProposalEditor = lazy(() => import(
  /* webpackPrefetch: true */
  /* webpackChunkName: "ProposalEditor" */
  'components/pages/Proposals/ProposalsView/ProposalEditor'
))

type ProcessModalType = 'Project' | 'AgendaTactical' | 'AgendaGovernance' | 'Proposal'

type Props = $ReadOnly<{
  organization: Organization,
  tension: Tension,
  viewer: Viewer,
  isOpenModal: (ProcessModalType | null) => boolean,
  closeModal: () => void,
  openModal: ({tensionDatabaseId: ?string, processModal: ProcessModalType | null}) => void,
}>

function ProcessingModals({
  organization,
  tension,
  viewer,
  isOpenModal,
  closeModal,
  openModal,
}: Props): Node {
  const asyncOpenModal = useCallback(({tensionDatabaseId, processModal}) => {
    openModal({tensionDatabaseId, processModal})
    return Promise.resolve()
  }, [openModal])

  // Temporary workaround until this form can be converted to Turbo/Hotwire
  const closeModalAndupdateRailsViewTensionRow = useCallback(async (tensionId) => {
    closeModal()
    await updateRailsViewTensionRow(organization.databaseId, viewer.databaseId, tensionId)
  }, [closeModal, organization.databaseId, viewer.databaseId])

  const handleClose = useCallback(() => {
    closeModalAndupdateRailsViewTensionRow(tension.databaseId)
  }, [closeModalAndupdateRailsViewTensionRow, tension.databaseId])

  const asyncCloseModal = useCallback(() => {
    closeModalAndupdateRailsViewTensionRow(tension.databaseId)
    return Promise.resolve()
  }, [closeModalAndupdateRailsViewTensionRow, tension.databaseId])

  const initialProposalId = useRef(tension.proposal?.databaseId)
  const proposalId = initialProposalId.current

  return (
    <Fragment>
      <TensionProcessModal
        tension={tension}
        isOpen={isOpenModal(null)}
        onClose={handleClose}
        openModal={asyncOpenModal}
      />
      {(isOpenModal('AgendaTactical')) && (
        <AgendaProcessModal
          tension={tension}
          close={handleClose}
          meetingType="TACTICAL"
        />
      )}
      {isOpenModal('AgendaGovernance') && (
        <AgendaProcessModal
          tension={tension}
          close={handleClose}
          meetingType="GOVERNANCE"
        />
      )}
      <Suspense fallback={<ModalLoader />}>
        {isOpenModal('Proposal') && proposalId && (
          <LazyProposalEditor
            orgDatabaseId={organization.databaseId}
            proposalDatabaseId={proposalId}
            toggle={asyncCloseModal}
          />
        )}
        {isOpenModal('Proposal') && !proposalId && (
          <LazyProposalCreator
            orgDatabaseId={organization.databaseId}
            tensionDatabaseId={tension.databaseId}
            toggle={asyncCloseModal}
          />
        )}
      </Suspense>
    </Fragment>
  )
}

export default (createFragmentContainer(ProcessingModals, {
  organization: graphql`
    fragment ProcessingModals_organization on Organization {
      databaseId
    }
  `,
  tension: graphql`
    fragment ProcessingModals_tension on Tension {
      databaseId

      proposal {
        databaseId
      }

      ...TensionProcessModal_tension
      ...AgendaProcessModal_tension
    }
  `,
  viewer: graphql`
    fragment ProcessingModals_viewer on Person {
      databaseId
    }
  `,
}): RelayFragmentContainer<typeof(ProcessingModals)>)
